// ── Select-to-assign queue (parent skill tree → kid's normal sessions) ──
//
// The parent skill tree no longer launches a per-skill practice session
// directly. Instead the parent SELECTS one or more skills to "introduce
// next"; those skill ids are persisted, in order, on the child's
// Progress (progress.assignedSkillIds — same per-child localStorage
// mechanism as completedStories). The kid then meets the assigned skills
// during their normal adaptive sessions: useTodaysMission prefers the
// head of this queue as the focal skill before falling through to the
// engine's chapter pick.
//
// This module is the PURE core (no React, no localStorage, no Supabase)
// so the queue rules are unit-testable in isolation. The storage shape
// lives on Progress (App/progress.ts); the read/inject point is
// useTodaysMission; the write point is the SkillTree screen.
//
// ── Design decisions encoded here ────────────────────────────────────
//  • ORDER MATTERS. The array is the order the parent tapped skills in,
//    oldest-first. The kid is steered to assignedSkillIds[0] first; once
//    that one is "done" (see below) it drops off and the next becomes the
//    head. This makes "introduce next" a literal FIFO queue.
//  • DE-DUPE on insert. Assigning an already-queued skill is a no-op (it
//    keeps its existing position — re-tapping doesn't reorder or
//    duplicate). Toggling a queued skill OFF removes it.
//  • DROP-WHEN-DONE (MA-302). A skill leaves the QUEUE once the kid has
//    MASTERED it (secure/fluent mastery band) OR has accumulated
//    ASSIGNED_QUEUE_ATTEMPT_CAP attempts regardless of mastery (bounded so
//    a persistently-struggling assigned skill can't pin the queue head
//    forever). A single attempt no longer drops it -- the parent/teacher's
//    "introduce this" intent should persist past the first touch, not
//    evaporate on it (MA-DEC-0721 #6; see docs/adr/ADR-089). Pruning is
//    applied on read (self-healing against attempts logged on other
//    devices) and the pruned queue is persisted back so it converges.
//    NOTE: this is DISTINCT from isSkillIntroduced (>=1 attempt), which
//    stays unchanged for progression.ts's assignedSkillEligible (the
//    extra-slot/pickAssignedExtraSkills surfacing rule) -- that surface
//    intentionally keeps the original "one touch and it stops being
//    surfaced as an extra" behavior; only the persisted-queue/focal-pick
//    rule changed.
//  • NULL-SAFE. A child with no assignedSkillIds field (every existing
//    user) reads as an empty queue; nothing about the prior behavior
//    changes until a parent assigns a skill.

import type { SkillState } from "@/types/progression";

/** The persisted queue shape. A plain ordered list of micro-skill ids
 *  (e.g. "ms.math.11"). Stored on Progress.assignedSkillIds. */
type AssignedSkillQueue = string[];

/** Normalize an untrusted persisted value into a clean queue: an array of
 *  non-empty strings, de-duped (first occurrence wins so order is stable),
 *  with falsy/garbage entries dropped. Legacy/absent -> []. */
export function normalizeAssignedQueue(raw: unknown): AssignedSkillQueue {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== "string") continue;
    const id = v.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Toggle a skill in the queue. If present -> remove it (parent un-assigned
 *  it). If absent -> append it to the TAIL (newly "introduce next", behind
 *  anything already queued). Returns a NEW array; never mutates the input. */
export function toggleAssignedSkill(
  queue: AssignedSkillQueue,
  skillId: string,
): AssignedSkillQueue {
  const norm = normalizeAssignedQueue(queue);
  if (norm.includes(skillId)) return norm.filter((id) => id !== skillId);
  return [...norm, skillId];
}

/** Whether a skill counts as INTRODUCED -- the kid has at least one recorded
 *  attempt on it. We read this from the SAME SkillState map the focal-pick
 *  engine uses (aggregated from child_attempts), so "introduced" means
 *  exactly "has appeared in a real session and been answered at least once".
 *  Absent from the map -> not introduced. */
export function isSkillIntroduced(
  skillId: string,
  skillStates: Record<string, SkillState>,
): boolean {
  const s = skillStates[skillId];
  return !!s && s.attemptsTotal > 0;
}

/** Whether a skill counts as MASTERED for the drop-from-queue rule: the
 *  aggregated mastery band is "secure" or "fluent". Mirrors the parent
 *  skill-tree's own "mastered" notion (the tree uses mastery_score >= 0.85,
 *  which the engine maps from these same bands). Absent -> not mastered. */
export function isSkillMastered(
  skillId: string,
  skillStates: Record<string, SkillState>,
): boolean {
  const band = skillStates[skillId]?.masteryBand;
  return band === "secure" || band === "fluent";
}

/** Attempt count at which an assigned skill drops from the QUEUE even if
 *  still unmastered (MA-302, MA-DEC-0721 #6) -- bounded so a struggling
 *  skill can't pin the queue head forever. Chosen to match the
 *  directPractice mastery-evidence window (MASTERY_POLICIES.directPractice
 *  .minimumAttempts, src/adaptation/masteryEvidence.ts) as a calibration
 *  anchor -- kept as its own local constant rather than imported, since
 *  that policy drives a different subsystem (Phase-C mastery
 *  classification) and coupling the two would make this queue-drop rule
 *  drift for reasons unrelated to it. */
// MA-1005: exported (unchanged) so the secured-skill lane's one-session
// fixture confirm (scripts/qa/driveSecuredSkillLive.mjs) reads this rule's own
// value instead of re-typing it.
export const ASSIGNED_QUEUE_ATTEMPT_CAP = 6;

/** Whether an assigned skill is DONE for QUEUE-PERSISTENCE purposes
 *  (MA-302): masteryBand secure/fluent, OR ASSIGNED_QUEUE_ATTEMPT_CAP
 *  attempts recorded regardless of mastery. Absent state -> false (matches
 *  isSkillMastered's null-safety). This is NOT the same predicate as
 *  isSkillIntroduced -- that one stays unchanged for progression.ts's
 *  assignedSkillEligible (extra-slot surfacing keeps its original
 *  first-touch behavior); this function is used ONLY by
 *  pruneAssignedQueue. */
export function isAssignedSkillQueueDone(
  skillId: string,
  skillStates: Record<string, SkillState>,
): boolean {
  const s = skillStates[skillId];
  return isSkillMastered(skillId, skillStates) || (!!s && s.attemptsTotal >= ASSIGNED_QUEUE_ATTEMPT_CAP);
}

/** Prune a skill from the queue once the kid is DONE with it per
 *  isAssignedSkillQueueDone (mastered, or ASSIGNED_QUEUE_ATTEMPT_CAP
 *  attempts recorded). Returns a NEW array with those skills removed,
 *  order otherwise preserved. Pure. */
export function pruneAssignedQueue(
  queue: AssignedSkillQueue,
  skillStates: Record<string, SkillState>,
): AssignedSkillQueue {
  return normalizeAssignedQueue(queue).filter((id) => !isAssignedSkillQueueDone(id, skillStates));
}

/** MA-61 assign slice — merges a linked student's CLOUD-assigned skill ids
 *  (written by a teacher via the assign_skill_to_student RPC, read off
 *  `cloudStudent.assigned_skill_ids`) into the kid's own LOCAL queue. Cloud
 *  entries not already present are appended AFTER the existing local
 *  entries (a parent's own local assigns keep priority; a teacher's
 *  assignment queues up behind them), de-duped, order preserved. `cloudIds`
 *  may be `undefined` (pre-migration-apply schema, or a student with no
 *  cloud row at all) -- that degrades to a no-op, same convention as every
 *  other schema-migration-independent cloud field (mascot_id,
 *  auto_read_enabled). Pure. */
export function mergeCloudAssignedSkills(
  localQueue: AssignedSkillQueue,
  cloudIds: unknown,
): AssignedSkillQueue {
  const local = normalizeAssignedQueue(localQueue);
  const cloud = normalizeAssignedQueue(cloudIds);
  if (cloud.length === 0) return local;
  const seen = new Set(local);
  const merged = [...local];
  for (const id of cloud) {
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push(id);
  }
  return merged;
}

/** Pick the focal skill the kid should be steered to from the assigned
 *  queue, if any. Walks the queue head->tail and returns the FIRST id that:
 *    1. is not already done (mastered, or 6 attempts -- pruneAssignedQueue /
 *       isAssignedSkillQueueDone), and
 *    2. has a non-empty served content pool (servedCountFor > 0).
 *  The pool gate mirrors useTodaysMission's RC-R focal gate: never steer a
 *  kid into a session the content pipeline can't render. Returns null when
 *  the queue is empty or no entry qualifies (caller falls through to the
 *  normal engine pick). Pure -- the pool lookup is injected. */
export function nextAssignedFocalSkill(
  queue: AssignedSkillQueue,
  skillStates: Record<string, SkillState>,
  servedCountFor: (skillId: string) => number,
): string | null {
  const live = pruneAssignedQueue(queue, skillStates);
  for (const id of live) {
    if (servedCountFor(id) > 0) return id;
  }
  return null;
}
